import dialog from './dialog.js';

/**
 * Caixa de progresso reutilizável (carregamentos PESADOS).
 *
 * Padrões de uso no SIAL:
 *   - Carregamento PADRÃO/RÁPIDO (< 2 s, sem percentual): use
 *       msgbox({ animation: true, text: 'Carregando produtos…' })
 *     que mostra o spinner padrão do SIAL.
 *   - Carregamento PESADO (export grande, geração de XLSX/CSV, download
 *     volumoso, processamento server-side longo): use ESTE componente.
 *
 * API:
 *   const p = progress({ title, text, indeterminate, cancellable, onCancel });
 *   p.set(fraction)        // 0..1 (ignorado se indeterminate)
 *   p.setText(text)
 *   p.setTitle(text)
 *   p.markStage(stageName) // texto auxiliar abaixo da barra
 *   p.destroy()
 */
const progress = function (args)
{
	args = args || {};
	const ind = !!args.indeterminate;
	const cancellable = !!args.cancellable;

	const html = `
		<div class="msgbox msgbox--progress" role="alertdialog" aria-live="polite" aria-busy="true">
			<div class="mb-title pg-title">${escHtml(args.title || 'Processando…')}</div>
			<div class="mb-text pg-text">${escHtml(args.text || '')}</div>
			<div class="pg-bar-wrap">
				<div class="pg-bar pg-bar--${ind ? 'indeterminate' : 'determinate'}">
					<div class="pg-bar-fill"></div>
				</div>
			</div>
			<div class="pg-meta">
				<span class="pg-stage" aria-live="polite"></span>
				<span class="pg-percent" aria-live="polite">${ind ? '' : '0%'}</span>
			</div>
			${cancellable ? '<button type="button" class="button bn_PgCancel mtl">Cancelar</button>' : ''}
		</div>`;

	const dlg = dialog.create(
		html,
		(key) =>
		{
			if (key === 'PgCancel' && typeof args.onCancel === 'function')
				args.onCancel();
		},
		/^$/,
	);

	const node = dlg.getNode();
	const fill = node.querySelector('.pg-bar-fill');
	const bar = node.querySelector('.pg-bar');
	const pct = node.querySelector('.pg-percent');
	const txt = node.querySelector('.pg-text');
	const ttl = node.querySelector('.pg-title');
	const stg = node.querySelector('.pg-stage');

	let destroyed = false;
	let isInd = ind;

	function promoteToDeterminate()
	{
		if (destroyed || !isInd || !bar) return;
		isInd = false;
		bar.classList.remove('pg-bar--indeterminate');
		bar.classList.add('pg-bar--determinate');
		if (fill) fill.style.width = '0%';
		if (pct) pct.textContent = '0%';
	}

	return {
		set(fraction)
		{
			if (destroyed) return;
			if (isInd) promoteToDeterminate();
			const f = Math.max(0, Math.min(1, Number(fraction) || 0));
			const p = Math.round(f * 100);
			if (fill) fill.style.width = p + '%';
			if (pct) pct.textContent = p + '%';
		},
		promote: promoteToDeterminate,
		setText(t)
		{
			if (destroyed)
				return;
			txt.textContent = String(t == null ? '' : t);
		},
		setTitle(t)
		{
			if (destroyed || !ttl)
				return;
			ttl.textContent = String(t == null ? '' : t);
		},
		markStage(t)
		{
			if (destroyed || !stg)
				return;
			stg.textContent = String(t == null ? '' : t);
		},
		destroy()
		{
			if (destroyed)
				return;
			destroyed = true;
			dlg.destroy();
		},
	};
};

/**
 * Lê uma Response do fetch como Blob, atualizando a barra de progresso conforme bytes recebidos.
 * Usa Content-Length quando disponível (% real); caso contrário mostra o total recebido.
 */
async function downloadResponseWithProgress(response, progressBox)
{
	const ctype = response.headers.get('Content-Type') || '';
	const total = Number(response.headers.get('Content-Length')) || 0;

	if (!response.body || typeof response.body.getReader !== 'function')
	{
		if (progressBox) progressBox.setText('Recebendo arquivo…');
		return await response.blob();
	}

	const reader = response.body.getReader();
	const chunks = [];
	let received = 0;

	// Quando há Content-Length confiável, promovemos a barra para determinate (0→100%).
	// Quando não há, simulamos progresso assintótico baseado em bytes recebidos.
	while (true)
	{
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
		received += value.length;
		if (progressBox)
		{
			if (total > 0)
			{
				progressBox.set(received / total);
				progressBox.markStage(`${formatBytes(received)} de ${formatBytes(total)}`);
			}
			else
			{
				// Sem Content-Length: aproxima percentual com curva assintótica
				// que tende a 95% para nunca passar antes do fim real.
				const approx = 1 - Math.exp(-received / (5 * 1024 * 1024)); // ~5MB → ~63%
				progressBox.set(Math.min(0.95, approx));
				progressBox.markStage(`Recebido: ${formatBytes(received)}`);
			}
		}
	}

	if (progressBox)
		progressBox.set(1);
	return new Blob(chunks, { type: ctype });
}

function formatBytes(n)
{
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
	return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function escHtml(s)
{
	const t = document.createElement('template');
	t.textContent = String(s == null ? '' : s);
	return t.innerHTML;
}

export { progress as default, downloadResponseWithProgress };
